import React from "react";
import { Button, Input, Tooltip } from "antd";
import { SendOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";

interface IChatInputBox {
  textValue: string;
  onTextValueChange: (value: string) => void;
  onSubmit: (value: string) => void;
  onEdit: (value: string) => void;
  isSendButtonDisabled?: boolean;
  isEditMode: boolean;
  isDisabled: boolean;
  onCancelEdit: () => void;
}

const Root = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 5px;
  padding: 16px 8px;
  @media ${(props) => props.theme.media.phone} {
    padding: 8px 8px;
  }
`;

const TextInput = styled(Input.TextArea)`
  background: #f5f5f5;
  font-size: 16px;
  line-height: 150%;
  &,
  &:focus {
    border: none;
    box-shadow: none;
  }
`;

export const ChatInputBox: React.FC<IChatInputBox> = ({
  textValue,
  onTextValueChange,
  onSubmit,
  onEdit,
  isSendButtonDisabled,
  isEditMode,
  onCancelEdit,
  isDisabled
}) => {
  const { t } = useTranslation("chats");
  const isPhone = false;
  const buttonSize = textValue.split("\n").length > 1 ? "large" : "middle";

  return (
    <Root>
      <TextInput

        disabled={isDisabled}
        value={textValue}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.ctrlKey && !e.shiftKey) {
            e.preventDefault();
            onSubmit(textValue);
            return false;
          }

          if (e.key === "Enter" && (e.ctrlKey || e.shiftKey || e.metaKey)) {
            e.preventDefault();
            onTextValueChange(textValue + "\n");
            return false;
          }
        }}
        onChange={(e) => {
          onTextValueChange(e.target.value);
        }}
        placeholder={
          isEditMode ? t("editMsg") : isPhone ? "Message" : t("editPlaceholder")
        }
        autoSize={{ minRows: 1, maxRows: 5 }}
      />
      {isEditMode ? (
        <Tooltip title={t("butCancelEdit")}>
          <Button
            disabled={isDisabled}
            type="default"
            size={buttonSize}
            onClick={() => {
              onCancelEdit();
            }}
          >
            {t("butCancel")}
          </Button>
        </Tooltip>
      ) : null}
      <Tooltip
        title={
          <div>
            <div>{t("sendTooltipLine1")}</div>
            <div>{t("sendTooltipLine2")}</div>
          </div>
        }
      >
        <Button
          type="primary"
          icon={<SendOutlined />}
          disabled={isSendButtonDisabled || !textValue || isDisabled}
          size={buttonSize}
          onClick={() => {
            if (isEditMode) {
              onEdit(textValue);
            } else {
              onSubmit(textValue);
            }
          }}
        >
          {t("send")}
        </Button>
      </Tooltip>
    </Root>
  );
};
